import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from "@/components/ui/sheet";
import { FileText, Loader2, RefreshCw, AlertTriangle } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { RoleGate } from "@/components/RoleGate";

interface Props {
  orgId?: string;
}

type Report = {
  id: string;
  report_type: string;
  period_start: string;
  period_end: string;
  executive_summary: string | null;
  top_issues: any;
  content: any;
  created_at: string;
};

export default function SentinelReports({ orgId }: Props) {
  const qc = useQueryClient();
  const [selected, setSelected] = useState<Report | null>(null);

  const { data: reports = [], isLoading } = useQuery({
    queryKey: ["sentinel-reports", orgId],
    enabled: !!orgId,
    queryFn: async () => {
      const { data, error } = await supabase
        .from("sentinel_reports")
        .select("*")
        .eq("org_id", orgId!)
        .order("created_at", { ascending: false })
        .limit(50);
      if (error) throw error;
      return (data || []) as Report[];
    },
  });

  const generate = useMutation({
    mutationFn: async () => {
      const { data, error } = await supabase.functions.invoke("sentinel-report", {
        body: { org_id: orgId, report_type: "on_demand", days: 7 },
      });
      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      toast.success("Relatório gerado");
      qc.invalidateQueries({ queryKey: ["sentinel-reports", orgId] });
    },
    onError: (e: any) => toast.error(`Erro: ${e.message ?? e}`),
  });

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <p className="text-sm text-muted-foreground">
          Relatórios executivos gerados pelo agente Report (sumário IA, top issues e métricas).
        </p>
        <RoleGate permission="sentinel.manage">
          <Button size="sm" disabled={generate.isPending || !orgId} onClick={() => generate.mutate()}>
            {generate.isPending
              ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" />
              : <RefreshCw className="w-4 h-4 mr-1.5" />}
            Gerar relatório (7d)
          </Button>
        </RoleGate>
      </div>

      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-sm font-medium flex items-center gap-2">
            <FileText className="w-4 h-4 text-primary" />
            Relatórios ({reports.length})
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-2">
          {isLoading ? (
            <><Skeleton className="h-16 w-full" /><Skeleton className="h-16 w-full" /></>
          ) : reports.length === 0 ? (
            <p className="py-6 text-center text-sm text-muted-foreground">
              Nenhum relatório ainda. Execute um Ciclo Completo ou clique em "Gerar relatório".
            </p>
          ) : (
            reports.map((r) => {
              const issues = Array.isArray(r.top_issues) ? r.top_issues.length : 0;
              return (
                <button
                  key={r.id}
                  type="button"
                  onClick={() => setSelected(r)}
                  className="w-full text-left p-3 rounded-md border border-border/60 hover:border-primary/40 transition-colors"
                >
                  <div className="flex items-center justify-between gap-2 mb-1">
                    <span className="text-sm font-medium">
                      {r.report_type === "on_demand" ? "Sob demanda" : r.report_type}
                    </span>
                    <div className="flex gap-1.5">
                      {issues > 0 && (
                        <Badge variant="outline" className="text-xs gap-1">
                          <AlertTriangle className="w-3 h-3" /> {issues} issues
                        </Badge>
                      )}
                      <Badge variant="outline" className="text-xs">
                        {new Date(r.created_at).toLocaleDateString("pt-BR")}
                      </Badge>
                    </div>
                  </div>
                  <p className="text-xs text-muted-foreground line-clamp-2">
                    {r.executive_summary?.slice(0, 200) ?? "—"}
                  </p>
                </button>
              );
            })
          )}
        </CardContent>
      </Card>

      <Sheet open={!!selected} onOpenChange={(o) => !o && setSelected(null)}>
        <SheetContent className="w-full sm:max-w-2xl overflow-y-auto">
          <SheetHeader>
            <SheetTitle>Relatório Executivo</SheetTitle>
            <SheetDescription>
              {selected && (
                <>
                  {new Date(selected.period_start).toLocaleDateString("pt-BR")} →{" "}
                  {new Date(selected.period_end).toLocaleDateString("pt-BR")} ·{" "}
                  gerado em {new Date(selected.created_at).toLocaleString("pt-BR")}
                </>
              )}
            </SheetDescription>
          </SheetHeader>
          {selected && (
            <div className="mt-4 space-y-5 text-sm">
              <div>
                <p className="text-xs font-medium text-muted-foreground mb-2">Sumário Executivo</p>
                <div className="whitespace-pre-wrap rounded-md bg-muted/50 p-3 leading-relaxed">
                  {selected.executive_summary || "—"}
                </div>
              </div>

              {selected.content && (
                <div>
                  <p className="text-xs font-medium text-muted-foreground mb-2">Métricas</p>
                  <div className="grid grid-cols-2 gap-2">
                    {Object.entries(selected.content as Record<string, any>).map(([k, v]) => (
                      <div key={k} className="rounded-md border border-border/60 p-2">
                        <p className="text-[10px] uppercase text-muted-foreground">{k.replace(/_/g, " ")}</p>
                        <p className="text-sm font-medium">
                          {typeof v === "object" ? JSON.stringify(v) : String(v)}
                        </p>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {Array.isArray(selected.top_issues) && selected.top_issues.length > 0 && (
                <div>
                  <p className="text-xs font-medium text-muted-foreground mb-2">
                    Top Issues ({selected.top_issues.length})
                  </p>
                  <div className="space-y-1.5">
                    {selected.top_issues.map((issue: any, i: number) => (
                      <div key={i} className="rounded-md border border-border/60 p-2 text-xs">
                        <div className="flex items-center justify-between">
                          <span className="font-medium">{issue.target}</span>
                          <Badge variant="outline">score {issue.score}</Badge>
                        </div>
                        <p className="text-muted-foreground mt-0.5">
                          {issue.dimension} · {issue.type}
                        </p>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
        </SheetContent>
      </Sheet>
    </div>
  );
}
